// Shared helpers for the guided-mode call report: full phase flow, timestamped notes.
// Used by Health.js (student summary + print) and RAOAdmin.js (instructor reprint).
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { StickyNote, Clock, ListChecks } from 'lucide-react';
import { formatChrono } from './PreArrivalPhase';

export const escHtml = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Resolve a multi-question answer key to its human label
const questionLabel = (phase, key) => {
  if (key === 'pain_scale') return 'Échelle de douleur (0-10)';
  const q = (phase.questions || []).find(qq => qq.id === key);
  return q ? (q.question || q.label || key) : key;
};

const isRedFlagAnswer = (phase, key, value) => {
  const q = (phase.questions || []).find(qq => qq.id === key);
  if (!q) return false;
  if (q.type === 'multi_choice') return (q.red_flag_options || []).includes(value);
  return !!(q.red_flag && String(value) === 'oui');
};

const displayValue = (phase, key, value) => {
  const q = (phase.questions || []).find(qq => qq.id === key);
  if (q && String(value) === 'oui' && q.yes_label) return q.yes_label;
  if (q && String(value) === 'non' && q.no_label) return q.no_label;
  return String(value);
};

// ---------- Print HTML builders ----------

export const buildPhasesHtml = (protocol, answers, phaseTimes) => {
  const pt = phaseTimes || {};
  const blocks = (protocol.phases || []).filter(ph => ph.type !== 'info_gathering').map(ph => {
    const val = answers?.[ph.id];
    if (val === undefined || val === null) return '';
    let content = '';
    if (ph.type === 'open_question' && Array.isArray(val)) {
      if (val.length === 0) return '';
      content = val.map(item => '<span style="display:inline-block;margin:2px 4px 2px 0;padding:3px 10px;border-radius:12px;background:#DCFCE7;color:#166534;font-size:12px;font-weight:600;">✓ ' + escHtml(item) + '</span>').join('');
    } else if (ph.type === 'multi_question' && typeof val === 'object') {
      const rows = Object.entries(val).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '').map(([k, v]) => {
        const rf = isRedFlagAnswer(ph, k, v);
        const vs = String(v);
        const bg = rf ? '#FEE2E2' : vs === 'oui' ? '#DCFCE7' : vs === 'inconnue' ? '#FEF3C7' : '#F8FAFC';
        const tc = rf ? '#DC2626' : vs === 'oui' ? '#166534' : vs === 'inconnue' ? '#92400E' : '#374151';
        return '<tr><td style="padding:3px 8px 3px 0;color:#374151;width:60%;vertical-align:top;font-size:13px;">' + (rf ? '⚠ ' : '') + escHtml(questionLabel(ph, k)) + '</td>'
          + '<td style="padding:3px 0;"><span style="display:inline-block;padding:2px 10px;border-radius:6px;font-size:12px;font-weight:600;background:' + bg + ';color:' + tc + ';">' + escHtml(displayValue(ph, k, v)) + '</span></td></tr>';
      }).join('');
      if (!rows) return '';
      content = '<table style="width:100%;border-collapse:collapse;">' + rows + '</table>';
    } else {
      const text = (typeof val === 'object') ? JSON.stringify(val) : String(val);
      if (!text.trim()) return '';
      const q = ph.question || ph.critical_question;
      content = (q ? '<div style="font-size:12px;color:#64748b;font-style:italic;margin-bottom:4px;">« ' + escHtml(q) + ' »</div>' : '')
        + '<div style="font-size:13px;color:#1e293b;font-weight:600;">→ ' + escHtml(text) + '</div>';
    }
    const chrono = pt[ph.id] != null
      ? '<span style="margin-left:auto;font-family:monospace;font-size:11px;color:#64748b;background:#f1f5f9;padding:2px 8px;border-radius:6px;">⏱ ' + formatChrono(pt[ph.id]) + '</span>'
      : '';
    return '<div style="margin-bottom:10px;padding:10px 12px;border:1px solid #e2e8f0;border-radius:8px;background:white;page-break-inside:avoid;">'
      + '<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">'
      + '<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:#2E6CF5;color:white;font-size:11px;font-weight:bold;flex-shrink:0;">' + escHtml(String(ph.number || '')) + '</span>'
      + '<span style="font-size:14px;font-weight:700;color:#002768;">' + escHtml(ph.title || ph.question || '') + '</span>'
      + chrono + '</div>' + content + '</div>';
  }).join('');
  if (!blocks) return '';
  return '<h3 style="font-size:15px;font-weight:bold;color:#002768;margin:18px 0 10px 0;">Déroulement complet de l\'appel — phase par phase</h3>' + blocks;
};

export const buildNotesHtml = (callNotes) => {
  if (!callNotes || callNotes.length === 0) return '';
  return '<div style="margin-top:16px;padding:12px;border-left:4px solid #F59E0B;background:#FFFBEB;border-radius:8px;page-break-inside:avoid;">'
    + '<h4 style="color:#92400E;font-size:14px;font-weight:bold;margin-bottom:8px;">📝 Notes prises pendant l\'appel (' + callNotes.length + ')</h4>'
    + '<table style="width:100%;border-collapse:collapse;font-size:13px;">'
    + callNotes.map(n => '<tr><td style="padding:3px 10px 3px 0;font-family:monospace;color:#B45309;font-weight:600;width:60px;vertical-align:top;">' + formatChrono(n.at || 0) + '</td>'
      + '<td style="padding:3px 0;color:#1e293b;">' + escHtml(n.text) + '</td></tr>').join('')
    + '</table></div>';
};

// ---------- React components ----------

// Persistent timestamped notes panel, visible during every phase of the guided call.
export const CallNotesPanel = ({ notes, onAddNote, getElapsed }) => {
  const [draft, setDraft] = useState('');
  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    onAddNote({ at: getElapsed(), text });
    setDraft('');
  };
  return (
    <div className="mt-3 p-3 rounded-xl border border-amber-200 bg-amber-50" data-testid="call-notes-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <StickyNote className="w-3.5 h-3.5 text-amber-600" />
        <span className="text-xs font-bold text-amber-800">Notes de l'appel</span>
        {notes.length > 0 && <span className="text-[10px] text-amber-600 ml-auto">{notes.length}</span>}
      </div>
      {notes.length > 0 && (
        <ul className="space-y-1 mb-2 max-h-40 overflow-y-auto" data-testid="call-notes-list">
          {notes.map((n, i) => (
            <li key={i} className="text-xs text-amber-900 flex gap-1.5">
              <span className="font-mono text-[10px] text-amber-600 flex-shrink-0 pt-0.5">{formatChrono(n.at || 0)}</span>
              <span>{n.text}</span>
            </li>
          ))}
        </ul>
      )}
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        placeholder="Noter un détail… (Entrée pour ajouter)"
        rows={2}
        className="w-full text-xs p-2 rounded-lg border border-amber-200 bg-white focus:outline-none focus:border-amber-400 resize-none"
        data-testid="call-notes-input"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!draft.trim()}
        className="w-full mt-1 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-40"
        style={{ backgroundColor: '#F59E0B' }}
        data-testid="call-notes-add-btn"
      >
        <Clock className="inline w-3 h-3 mr-1" />Ajouter (horodaté)
      </button>
    </div>
  );
};

// Summary card: notes list
export const CallNotesCard = ({ notes }) => {
  if (!notes || notes.length === 0) return null;
  return (
    <Card className="border-amber-200 bg-amber-50" data-testid="summary-call-notes">
      <CardContent className="pt-4">
        <h4 className="text-sm font-bold text-amber-800 mb-2">📝 Notes prises pendant l'appel ({notes.length})</h4>
        <ul className="space-y-1">
          {notes.map((n, i) => (
            <li key={i} className="text-sm text-amber-900 flex gap-2">
              <span className="font-mono text-xs text-amber-600 flex-shrink-0 pt-0.5">{formatChrono(n.at || 0)}</span>
              <span>{n.text}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// Summary card: full phase-by-phase call flow
export const CallFlowCard = ({ protocol, allAnswers, phaseTimes }) => {
  const pt = phaseTimes || {};
  const phases = (protocol?.phases || []).filter(ph => {
    if (ph.type === 'info_gathering') return false;
    const val = allAnswers?.[ph.id];
    if (val === undefined || val === null) return false;
    if (Array.isArray(val) && val.length === 0) return false;
    return true;
  });
  if (phases.length === 0) return null;
  return (
    <Card className="border-0 glass" data-testid="summary-call-flow">
      <CardContent className="pt-4">
        <div className="flex items-center gap-1.5 mb-3">
          <ListChecks className="w-4 h-4" style={{ color: '#002768' }} />
          <h4 className="text-sm font-bold" style={{ color: '#002768' }}>Déroulement complet de l'appel</h4>
        </div>
        <div className="space-y-2.5">
          {phases.map(ph => {
            const val = allAnswers[ph.id];
            return (
              <div key={ph.id} className="p-2.5 rounded-lg border border-gray-100 bg-white/60">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: '#2E6CF5' }}>{ph.number}</span>
                  <span className="text-xs font-bold text-gray-700">{ph.title || ph.question}</span>
                  {pt[ph.id] != null && (
                    <span className="ml-auto font-mono text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">⏱ {formatChrono(pt[ph.id])}</span>
                  )}
                </div>
                {ph.type === 'open_question' && Array.isArray(val) ? (
                  <div className="flex flex-wrap gap-1">
                    {val.map(item => <span key={item} className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">✓ {item}</span>)}
                  </div>
                ) : ph.type === 'multi_question' && typeof val === 'object' ? (
                  <div className="space-y-0.5">
                    {Object.entries(val).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '').map(([k, v]) => {
                      const rf = isRedFlagAnswer(ph, k, v);
                      return (
                        <div key={k} className="flex justify-between items-center gap-2 text-xs">
                          <span className={rf ? 'text-red-600 font-medium' : 'text-gray-600'}>{rf ? '⚠ ' : ''}{questionLabel(ph, k)}</span>
                          <span className={`px-1.5 py-0.5 rounded font-semibold flex-shrink-0 ${rf ? 'bg-red-100 text-red-700' : String(v) === 'oui' ? 'bg-green-100 text-green-700' : String(v) === 'inconnue' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{displayValue(ph, k, v)}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs">
                    {(ph.question || ph.critical_question) && <span className="text-gray-400 italic">« {ph.question || ph.critical_question} » </span>}
                    <span className="font-semibold text-gray-800">→ {typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
